import { memo, useState, useRef, useEffect, useCallback } from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import type { DataNode } from '@/types'
import { getValueDisplay } from '@/utils/parsers'
import { useEditorStore } from '@/store'

const typeColors: Record<string, string> = {
  string: 'bg-emerald-500 dark:bg-emerald-600',
  number: 'bg-blue-500 dark:bg-blue-600',
  boolean: 'bg-amber-500 dark:bg-amber-600',
  null: 'bg-slate-400 dark:bg-slate-500',
  object: 'bg-violet-500 dark:bg-violet-600',
  array: 'bg-pink-500 dark:bg-pink-600',
  root: 'bg-slate-700 dark:bg-slate-300',
}

const typeBorders: Record<string, string> = {
  string: 'border-emerald-300 dark:border-emerald-700',
  number: 'border-blue-300 dark:border-blue-700',
  boolean: 'border-amber-300 dark:border-amber-700',
  null: 'border-slate-300 dark:border-slate-600',
  object: 'border-violet-300 dark:border-violet-700',
  array: 'border-pink-300 dark:border-pink-700',
  root: 'border-slate-400 dark:border-slate-500',
}

function DataNodeComponent({ data, selected }: NodeProps<DataNode>) {
  const { label, type, childCount, value, path } = data
  const valueDisplay = getValueDisplay({ data } as DataNode)
  const isContainer = type === 'object' || type === 'array'
  const isEditable = !isContainer && type !== 'root'

  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const updateNodeValue = useEditorStore((state) => state.updateNodeValue)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleDoubleClick = useCallback(() => {
    if (!isEditable) return
    // Set initial edit value based on type
    if (type === 'string') {
      setEditValue(String(value ?? ''))
    } else if (type === 'boolean') {
      setEditValue(String(value))
    } else if (type === 'null') {
      setEditValue('null')
    } else {
      setEditValue(String(value ?? ''))
    }
    setIsEditing(true)
  }, [isEditable, type, value])

  const handleSave = useCallback(() => {
    if (!isEditing) return

    let parsedValue: unknown = editValue

    // Parse value based on original type
    if (type === 'number') {
      const num = parseFloat(editValue)
      if (!isNaN(num)) {
        parsedValue = num
      }
    } else if (type === 'boolean') {
      parsedValue = editValue.toLowerCase() === 'true'
    } else if (type === 'null') {
      if (editValue.toLowerCase() === 'null' || editValue === '') {
        parsedValue = null
      }
    }
    // string stays as string

    updateNodeValue(path, parsedValue)
    setIsEditing(false)
  }, [isEditing, editValue, type, path, updateNodeValue])

  const handleCancel = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation()
      if (e.key === 'Enter') {
        handleSave()
      } else if (e.key === 'Escape') {
        handleCancel()
      }
    },
    [handleSave, handleCancel]
  )

  return (
    <div
      className={`
        group relative min-w-[120px] max-w-[280px]
        rounded-lg border-2 shadow-md
        bg-white dark:bg-slate-800
        transition-all duration-200 ease-out
        hover:shadow-lg hover:scale-[1.02]
        ${typeBorders[type]}
        ${selected ? 'ring-2 ring-primary-500 ring-offset-2 dark:ring-offset-slate-900' : ''}
      `}
    >
      {/* Target handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-slate-400 dark:!bg-slate-500 !border-2 !border-white dark:!border-slate-800"
      />

      {/* Node content */}
      <div className="px-3 py-2">
        {/* Header with type badge */}
        <div className="flex items-center gap-2 mb-1">
          <span
            className={`
              inline-flex items-center justify-center
              w-2 h-2 rounded-full
              ${typeColors[type]}
            `}
            title={type}
          />
          <span className="font-semibold text-sm text-slate-700 dark:text-slate-200 truncate">
            {label}
          </span>
          {isContainer && (
            <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">
              {childCount}
            </span>
          )}
        </div>

        {/* Value display / Edit input */}
        {!isContainer && (
          <>
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                className="w-full text-xs font-mono px-1 py-0.5 rounded border border-primary-400 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 outline-none focus:ring-1 focus:ring-primary-500"
              />
            ) : (
              <div
                className={`text-xs text-slate-500 dark:text-slate-400 font-mono truncate ${isEditable ? 'cursor-pointer hover:text-primary-600 dark:hover:text-primary-400' : ''}`}
                onDoubleClick={handleDoubleClick}
                title={isEditable ? 'Double-click to edit' : undefined}
              >
                {valueDisplay}
              </div>
            )}
          </>
        )}

        {/* Type indicator for containers */}
        {isContainer && (
          <div className="text-xs text-slate-400 dark:text-slate-500 font-mono">
            {type === 'array' ? `Array[${childCount}]` : `Object{${childCount}}`}
          </div>
        )}
      </div>

      {/* Source handle */}
      {isContainer && (
        <Handle
          type="source"
          position={Position.Right}
          className="!w-3 !h-3 !bg-slate-400 dark:!bg-slate-500 !border-2 !border-white dark:!border-slate-800"
        />
      )}

      {/* Hover overlay with path */}
      <div
        className={`
          absolute -bottom-8 left-0 right-0
          opacity-0 group-hover:opacity-100
          transition-opacity duration-200
          pointer-events-none z-10
        `}
      >
        <div className="tooltip mx-auto w-fit">
          {data.path}
        </div>
      </div>
    </div>
  )
}

export default memo(DataNodeComponent)
